import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { Database } from '../services/Database';

const DEFAULT_LESSON_THRESHOLD = 5;
const DEFAULT_EXPIRY_ALERT_DAYS = 30;

export interface AlertSettings {
    lessonThreshold: number;
    expiryAlertDays: number;
}

interface AlertSettingsContextType {
    lessonThreshold: number;
    expiryAlertDays: number;
    /** 是否已從伺服器載入完成，用於避免設定頁面載入時先顯示預設值再閃爍 */
    loaded: boolean;
    refreshAlertSettings: () => Promise<void>;
}

const AlertSettingsContext = createContext<AlertSettingsContextType>({
    lessonThreshold: DEFAULT_LESSON_THRESHOLD,
    expiryAlertDays: DEFAULT_EXPIRY_ALERT_DAYS,
    loaded: false,
    refreshAlertSettings: async () => {},
});

export const AlertSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { currentUser } = useAuth();
    const [settings, setSettings] = useState<AlertSettings>({
        lessonThreshold: DEFAULT_LESSON_THRESHOLD,
        expiryAlertDays: DEFAULT_EXPIRY_ALERT_DAYS,
    });
    const [loaded, setLoaded] = useState(false);

    const refreshAlertSettings = useCallback(async () => {
        if (!currentUser) return;
        try {
            const s = await Database.getUserSettings();
            setSettings({
                lessonThreshold: s?.lessonAlertThreshold ?? DEFAULT_LESSON_THRESHOLD,
                expiryAlertDays: s?.expiryAlertDays ?? DEFAULT_EXPIRY_ALERT_DAYS,
            });
        } catch (e) {
            console.error('載入警示設定失敗:', e);
        } finally {
            setLoaded(true);
        }
    }, [currentUser]);

    useEffect(() => {
        if (!currentUser) {
            setLoaded(true);
            return;
        }
        refreshAlertSettings();
    }, [refreshAlertSettings, currentUser]);

    const value = {
        lessonThreshold: settings.lessonThreshold,
        expiryAlertDays: settings.expiryAlertDays,
        loaded,
        refreshAlertSettings,
    };

    return (
        <AlertSettingsContext.Provider value={value}>
            {children}
        </AlertSettingsContext.Provider>
    );
};

export const useAlertSettings = () => useContext(AlertSettingsContext);
